/**
 * The one stamp formatter of the client (R1544).
 *
 * Every instant the client shows takes an ISO 8601 form, in every
 * language (ui-map-interface.md §8 "Time displays"): `YYYY-MM-DD HH:MM`
 * for a date with a time, `HH:MM` for a time alone, `YYYY-MM-DD` for a
 * date alone. The clock
 * is the 24-hour clock, every field is zero-padded, and a space stands in
 * place of the `T`. The forms never go through `toLocale*` or
 * `Intl.DateTimeFormat`: those answer with AM and PM, a month name or
 * another date order by machine, and two players must read one stamp.
 *
 * A stamp is in the reader's local time unless the caller asks for UTC,
 * and a UTC stamp ends in ` UTC`. A local stamp has no mark, unless the
 * caller asks for the zone mark (`zone`, R1752): then it ends in the short
 * name of the reader's zone at that instant, `22:55 CEST`. The zone name
 * is the one field that goes through `Intl.DateTimeFormat`, because only
 * the browser knows the name of its zone. A duration is not an instant,
 * and none of these functions writes one.
 *
 * The module is a leaf: it imports nothing, so the maintenance cover, the
 * hub and the arcade page all read it at no cost.
 */

/** What a stamp shows besides its default fields. */
export interface StampOpts {
  /** Show `:SS` too. Only a display that needs the second asks for it. */
  seconds?: boolean;
  /** Read the instant in UTC and end the stamp in ` UTC`. */
  utc?: boolean;
  /** End a local stamp in the short name of the reader's zone at that
   *  instant (R1752). A UTC stamp ignores it: it has its own mark. */
  zone?: boolean;
  /** The language the zone name is written in, a BCP 47 tag. Absent, the
   *  browser's default language writes it. */
  locale?: string;
}

/** What `zoneName` reads besides the instant. */
export interface ZoneOpts {
  /** The language of the name, a BCP 47 tag. */
  locale?: string;
  /** An IANA zone in place of the browser's own, for example
   *  `Europe/Berlin`. */
  timeZone?: string;
}

const zoneFormats = new Map<string, Intl.DateTimeFormat>();

/**
 * The short name of a time zone at one instant: `CEST` or `GMT+2` in
 * English, `MESZ` in German. The name follows the summer time of the
 * instant. A figure that is no stamp, or a browser that names no zone,
 * reads ''.
 */
export function zoneName(unixSec: number, opts: ZoneOpts = {}): string {
  const d = dateOf(unixSec);
  if (d === null) return '';
  const key = `${opts.locale ?? ''}|${opts.timeZone ?? ''}`;
  try {
    let f = zoneFormats.get(key);
    if (f === undefined) {
      f = new Intl.DateTimeFormat(opts.locale, { timeZoneName: 'short', timeZone: opts.timeZone });
      zoneFormats.set(key, f);
    }
    return f.formatToParts(d).find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/**
 * The IANA name of the reader's own zone, `Europe/Berlin`, as a row that
 * explains a local stamp names it (R1791). A browser that names no zone
 * reads ''. The name is read once: the zone of a page does not change
 * under it.
 */
export function zoneId(): string {
  if (ownZone === undefined) {
    try {
      ownZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
    } catch {
      ownZone = '';
    }
  }
  return ownZone;
}
let ownZone: string | undefined;

/**
 * The language a zone name is written in: the first browser language of
 * the page's own language, because it carries the region (`en-GB` writes
 * CEST where `en` writes GMT+2), else the page's language itself.
 */
export function zoneLocale(page: string, browser: readonly string[]): string {
  const base = page.toLowerCase().split('-')[0];
  return browser.find((t) => t.toLowerCase().split('-')[0] === base) ?? page;
}

/** The suffix a stamp ends in: ` UTC`, ` <zone>` or nothing. */
function markOf(d: Date, opts: StampOpts): string {
  if (opts.utc === true) return UTC_MARK;
  if (opts.zone !== true) return '';
  const z = zoneName(d.getTime() / 1000, { locale: opts.locale });
  return z === '' ? '' : ` ${z}`;
}

/** The mark a UTC stamp ends in. */
export const UTC_MARK = ' UTC';

/** A clock field, two digits wide. */
export function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** The date of a unix second, or null where the figure is no stamp. A
 *  stamp of 0 is "never", so it reads as no stamp and not as the epoch. */
function dateOf(unixSec: number): Date | null {
  if (!Number.isFinite(unixSec) || unixSec <= 0) return null;
  const d = new Date(unixSec * 1000);
  return Number.isNaN(d.getTime()) ? null : d;
}

function datePart(d: Date, utc: boolean): string {
  const y = utc ? d.getUTCFullYear() : d.getFullYear();
  const m = utc ? d.getUTCMonth() : d.getMonth();
  const day = utc ? d.getUTCDate() : d.getDate();
  return `${y}-${pad2(m + 1)}-${pad2(day)}`;
}

function timePart(d: Date, utc: boolean, seconds: boolean): string {
  const h = utc ? d.getUTCHours() : d.getHours();
  const m = utc ? d.getUTCMinutes() : d.getMinutes();
  const hm = `${pad2(h)}:${pad2(m)}`;
  if (!seconds) return hm;
  return `${hm}:${pad2(utc ? d.getUTCSeconds() : d.getSeconds())}`;
}

/** `YYYY-MM-DD`. A figure that is no stamp reads ''. */
export function isoDate(unixSec: number, opts: StampOpts = {}): string {
  const d = dateOf(unixSec);
  if (d === null) return '';
  return datePart(d, opts.utc === true) + markOf(d, opts);
}

/** `HH:MM`, or `HH:MM:SS` with `seconds`. A figure that is no stamp reads ''. */
export function isoTime(unixSec: number, opts: StampOpts = {}): string {
  const d = dateOf(unixSec);
  if (d === null) return '';
  return timePart(d, opts.utc === true, opts.seconds === true) + markOf(d, opts);
}

/** `YYYY-MM-DD HH:MM`, or with `:SS`. A figure that is no stamp reads ''. */
export function isoDateTime(unixSec: number, opts: StampOpts = {}): string {
  const d = dateOf(unixSec);
  if (d === null) return '';
  const utc = opts.utc === true;
  return `${datePart(d, utc)} ${timePart(d, utc, opts.seconds === true)}` + markOf(d, opts);
}

/**
 * The time alone where `at` falls on the same day as `now`, and the date
 * with the time on any other day. A countdown target ("back at 14:05")
 * reads short today, and it never hides that it is tomorrow.
 */
export function isoNear(unixSec: number, nowSec: number, opts: StampOpts = {}): string {
  const d = dateOf(unixSec);
  if (d === null) return '';
  const now = dateOf(nowSec);
  const utc = opts.utc === true;
  if (now !== null && datePart(now, utc) === datePart(d, utc)) return isoTime(unixSec, opts);
  return isoDateTime(unixSec, opts);
}
